import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Brain, 
  Search, 
  Calculator, 
  CheckCircle, 
  Globe, 
  BarChart3,
  Target,
  Lightbulb,
  X
} from 'lucide-react';

interface VWBALoaderProps {
  isVisible: boolean;
  onClose?: () => void;
}

interface AnalysisStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  duration: number;
  color: string;
}

const VWBALoader: React.FC<VWBALoaderProps> = ({ isVisible, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const analysisSteps: AnalysisStep[] = [
    {
      id: 'fact-sheet-intake',
      title: 'Fact Sheet Intake & Scoping (VWBA)',
      description: 'Reading the implementing partner\'s fact sheet to capture activity type, watershed, boundaries, baseline, and timeline',
      icon: <FileText className="w-6 h-6" />,
      duration: 5000,
      color: 'text-blue-600'
    },
    {
      id: 'vwba-decomposition',
      title: 'VWBA Project Decomposition',
      description: 'Translating the description into VWBA terms—hydrologic pathway (infiltration, evapotranspiration, leakage, return-flow), impact unit, and beneficiaries',
      icon: <Search className="w-6 h-6" />,
      duration: 5500,
      color: 'text-purple-600'
    },
    {
      id: 'vwba-method-match',
      title: 'VWBA Method Match',
      description: 'Selecting the correct VWBA calculation pathway for this activity and checking eligibility: additionality, materiality, and no double-counting',
      icon: <Target className="w-6 h-6" />,
      duration: 6000,
      color: 'text-green-600'
    },
    {
      id: 'inputs-evidence-harvest',
      title: 'Inputs & Evidence Harvest',
      description: 'Collecting required parameters from the fact sheet (area treated, soils/cover, slopes, device specs, flow rates) and validating sources, units, and monitoring plans',
      icon: <BarChart3 className="w-6 h-6" />,
      duration: 5500,
      color: 'text-orange-600'
    },
    {
      id: 'benefit-run-confidence',
      title: 'Benefit Run & Confidence',
      description: 'Configuring equations and boundaries, estimating annual volumetric benefit (actual vs anticipated), and scoring uncertainty with clear next-best-data asks',
      icon: <Calculator className="w-6 h-6" />,
      duration: 6000,
      color: 'text-cyan-600'
    }
  ];

  useEffect(() => {
    if (!isVisible) {
      setCurrentStep(0);
      setCompletedSteps([]);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);
    let stepIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const processStep = () => {
      if (stepIndex < analysisSteps.length) {
        setCurrentStep(stepIndex);
        setCompletedSteps(prev => [...prev, stepIndex]);
        
        // Get the actual duration for the current step
        const currentStepDuration = analysisSteps[stepIndex]?.duration || 4000;
        // Add some randomness to make it feel more realistic
        const randomDelay = Math.random() * 1000 + 500;
        const totalDelay = currentStepDuration + randomDelay;
        
        timeoutId = setTimeout(() => {
          stepIndex++;
          if (stepIndex >= analysisSteps.length) {
            setIsAnimating(false);
          } else {
            processStep();
          }
        }, totalDelay);
      }
    };

    // Start the first step
    processStep();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const currentStepData = analysisSteps[currentStep];
  const progress = ((currentStep + 1) / analysisSteps.length) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4 relative">
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 z-10"
            aria-label="Close loader"
          >
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        )}
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center animate-pulse">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            VWBA Analysis in Progress
          </h2>
          <p className="text-gray-600">
            Processing your document...
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Current Step */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200">
            <div className={`flex-shrink-0 ${currentStepData?.color || 'text-gray-600'} ${
              isAnimating ? 'animate-bounce' : ''
            }`}>
              {currentStepData?.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {currentStepData?.title || 'Processing...'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentStepData?.description || 'Analyzing your document...'}
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        </div>

        {/* Steps List */}
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {analysisSteps.map((step, index) => {
            const isCompleted = completedSteps.includes(index);
            const isCurrent = index === currentStep;

            return (
              <div
                key={step.id}
                className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-50 border border-green-200'
                    : isCurrent
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className={`flex-shrink-0 ${
                  isCompleted
                    ? 'text-green-600'
                    : isCurrent
                    ? step.color
                    : 'text-gray-400'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    step.icon
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    isCompleted
                      ? 'text-green-800'
                      : isCurrent
                      ? 'text-gray-900'
                      : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                  <p className={`text-xs ${
                    isCompleted
                      ? 'text-green-600'
                      : isCurrent
                      ? 'text-gray-600'
                      : 'text-gray-400'
                  }`}>
                    {step.description}
                  </p>
                </div>
                {isCurrent && (
                  <div className="flex-shrink-0">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Fun Facts */}
        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
          <div className="flex items-center space-x-2 mb-2">
            <Lightbulb className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">Did you know?</span>
          </div>
          <p className="text-xs text-purple-700">
            VWBA methodologies can help quantify water benefits worth millions of dollars 
            in ecosystem services, supporting both environmental and economic sustainability.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VWBALoader;
